import React, { useEffect, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { TrendingUp, TrendingDown } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 10000;

// ISO-2 country code → emoji flag (works on macOS/iOS/Android; Windows falls
// back to plain letters which is acceptable for this widget).
const flagFor = (code) => {
  if (!code || code.length !== 2) return "";
  const A = 0x1f1e6;
  const base = "A".charCodeAt(0);
  return String.fromCodePoint(
    A + code.charCodeAt(0) - base,
    A + code.charCodeAt(1) - base,
  );
};

const COPY = {
  nl: { live: "mensen nu actief", waitlist: "mensen op de waitlist" },
  en: { live: "people online now", waitlist: "people on the waitlist" },
  de: { live: "Personen jetzt aktiv", waitlist: "Personen auf der Warteliste" },
  fr: { live: "personnes en ligne", waitlist: "personnes sur la liste d'attente" },
  es: { live: "personas en línea", waitlist: "personas en la lista de espera" },
};

/**
 * Anonymous social-proof badge.
 *
 * Variants:
 *  - default (light) → used in Hero
 *  - compact + dark  → used in AnnouncementBar
 *
 * Shows live visitors (last 5 min) or — if zero — total waitlist size,
 * plus up to 3 country flags from the last hour of traffic.
 */
export default function LiveCounter({ variant = "default" }) {
  const { i18n } = useTranslation();
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data: d } = await axios.get(`${API}/api/stats/live`);
        if (!cancelled) setData(d);
      } catch {
        /* silent — widget stays hidden */
      }
    };
    load();
    const id = setInterval(load, POLL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  if (!data || !data.display) return null;

  const isLive = data.mode === "live";
  const locale = i18n.language || "nl-NL";
  const number = data.display.toLocaleString(locale);
  const base = locale.split("-")[0];
  const c = COPY[base] || COPY.en;
  const label = isLive ? c.live : c.waitlist;

  const isDark = variant === "dark";
  const compact = variant === "dark" || variant === "compact";

  const wrapperCls = isDark
    ? "inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-2.5 py-1 text-[11px] text-[hsl(var(--background))] backdrop-blur-sm"
    : "inline-flex items-center gap-2 rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-3.5 py-1.5 text-xs text-[hsl(var(--ink))]";

  const numberCls = "font-medium tabular-nums";
  const labelCls = isDark
    ? "text-[hsl(var(--background))]/70"
    : "text-[hsl(var(--ink-soft))]";

  const topCountries = (data.top_countries || []).slice(0, 3);
  const trend = data.trend || null;
  // Only show trend pill when in live mode AND delta is non-zero AND we actually
  // had some traffic in either window (avoids "+0" / "−0" noise on quiet sites).
  const showTrend =
    isLive &&
    trend &&
    trend.direction !== "flat" &&
    (trend.current_10min > 0 || trend.previous_10min > 0);

  return (
    <div
      data-testid="live-counter"
      data-mode={data.mode}
      data-variant={variant}
      className={wrapperCls}
      aria-live="polite"
    >
      <span className="relative flex h-2 w-2" aria-hidden="true">
        {isLive && (
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
        )}
        <span
          className={`relative inline-flex h-2 w-2 rounded-full ${
            isLive
              ? "bg-emerald-500"
              : isDark ? "bg-[hsl(var(--sand))]" : "bg-[hsl(var(--sand-dark))]"
          }`}
        />
      </span>
      <span data-testid="live-counter-text" className="whitespace-nowrap">
        <span className={numberCls}>{number}</span>{" "}
        <span className={labelCls}>{label}</span>
      </span>
      {showTrend && (
        <TrendPill trend={trend} isDark={isDark} />
      )}
      {topCountries.length > 0 && (
        <span
          data-testid="live-counter-countries"
          className={`ml-1 hidden items-center gap-0.5 border-l ${isDark ? "border-white/15" : "border-[hsl(var(--border))]"} pl-2 text-base leading-none sm:inline-flex`}
          title={`Top landen laatste uur: ${topCountries.map((tc) => tc.code).join(", ")}`}
        >
          {topCountries.map((tc) => (
            <span
              key={tc.code}
              data-testid={`flag-${tc.code}`}
              aria-label={tc.code}
              className={compact ? "text-sm" : "text-base"}
            >
              {flagFor(tc.code) || tc.code}
            </span>
          ))}
        </span>
      )}
    </div>
  );
}

// Subtle FOMO trend pill — shows +X or −X with directional arrow.
function TrendPill({ trend, isDark }) {
  const up = trend.direction === "up";
  const sign = up ? "+" : "−";
  const value = Math.abs(trend.delta);
  const Icon = up ? TrendingUp : TrendingDown;
  const baseCls = isDark
    ? up
      ? "bg-emerald-400/20 text-emerald-200"
      : "bg-rose-400/20 text-rose-200"
    : up
      ? "bg-emerald-500/15 text-emerald-700"
      : "bg-rose-500/15 text-rose-700";
  return (
    <span
      data-testid="live-counter-trend"
      data-direction={trend.direction}
      title={`${sign}${value} sessies tov de 10 min daarvoor`}
      className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-semibold tabular-nums ${baseCls}`}
    >
      <Icon className="h-2.5 w-2.5" strokeWidth={2.2} />
      {sign}{value}
    </span>
  );
}
